'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLogin } from '@/lib/hooks/useAuth';
import { getDashboardPathForRole, setTokens } from '@/lib/utils/auth';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; password?: string }>({});

  const loginMutation = useLogin({
    onSuccess: (data) => {
      // Lưu tokens vào localStorage và cookie
      if (data.access_token && data.refresh_token) {
        setTokens(data.access_token, data.refresh_token, rememberMe);
      }
      
      // Redirect về trang được yêu cầu hoặc trang chủ
      const redirect = searchParams.get('redirect');
      const safeRedirect = redirect && redirect.startsWith('/') ? redirect : '';
      const role = data?.user?.role;
      const isBoard = Boolean((data?.user as any)?.is_board);
      const targetFromRole = getDashboardPathForRole(role, isBoard);
      const isAdmin = String(role || '').toUpperCase() === 'ADMIN' || isBoard;
      const normalizedRedirect = !safeRedirect
        ? ''
        : isAdmin
          ? safeRedirect.replace(/^\/dashboard(\/|$)/, '/admin$1')
          : safeRedirect.replace(/^\/admin(\/|$)/, '/dashboard$1');
      const nextPath =
        normalizedRedirect &&
        (isAdmin ||
          (!normalizedRedirect.startsWith('/admin/staff') &&
            !normalizedRedirect.startsWith('/admin/departments/permissions') &&
            !normalizedRedirect.startsWith('/dashboard/staff') &&
            !normalizedRedirect.startsWith('/dashboard/departments/permissions')))
          ? normalizedRedirect
          : targetFromRole;
      router.replace(nextPath);
    },
    showErrorToast: true,
  });

  const validateForm = () => {
    const newErrors: { phone?: string; password?: string } = {};

    if (!phoneNumber.trim()) {
      newErrors.phone = 'Vui lòng nhập số điện thoại';
    }

    if (!password) {
      newErrors.password = 'Vui lòng nhập mật khẩu';
    } else if (password.length < 6) {
      newErrors.password = 'Mật khẩu phải có ít nhất 6 ký tự';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginMutation.isPending) return;
    
    if (!validateForm()) {
      return;
    }

    setErrors({});
    loginMutation.mutate({
      phone: phoneNumber.trim(),
      password,
      rememberMe,
    });
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">
          Chào mừng quay trở lại!!
        </h2>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Đăng nhập</h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Phone Field */}
        <div>
          <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
            Số điện thoại
          </label>
          <input
            id="phone_number"
            type="text"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (errors.phone) {
                setErrors({ ...errors, phone: undefined });
              }
            }}
            placeholder="Nhập số điện thoại"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.phone
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
            disabled={loginMutation.isPending}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Mật khẩu
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: undefined });
                }
              }}
              placeholder="Nhập mật khẩu"
              className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                errors.password
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
              disabled={loginMutation.isPending}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password}</p>
          )}
        </div>

        {/* Remember Me + Forgot password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={loginMutation.isPending}
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
              Ghi nhớ đăng nhập
            </label>
          </div>
          <Link href="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-700">
            Quên mật khẩu?
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loginMutation.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
        </button>
      </form>

      {/* Footer */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          © Aplaco Project Management. All right reserved.
        </p>
      </div>
    </div>
  );
}
